import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ShieldAlert, Search, Loader2, Trash2, Lock, CheckCircle2, Camera } from "lucide-react";
import { toast } from "sonner";

/** Admin — Nettoyage de la base : simulation, sélection des comptes à conserver, double confirmation. */
export default function AdminPurge() {
  const [data, setData] = useState(null);
  const [keep, setKeep] = useState(new Set());
  const [q, setQ] = useState("");
  const [confirm, setConfirm] = useState("");
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const load = () => api.get("/admin/purge/preview").then(({ data: d }) => {
    setData(d);
    setKeep(new Set(d.users.filter((u) => u.suggested_keep).map((u) => u.user_id)));
  }).catch((e) => toast.error(e.userMessage || "Chargement impossible"));
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!data) return [];
    const n = q.trim().toLowerCase();
    return data.users.filter((u) => !n || (u.name || "").toLowerCase().includes(n) || (u.email || "").toLowerCase().includes(n));
  }, [data, q]);

  const toggle = (u) => {
    if (u.locked) return;
    setKeep((prev) => { const s = new Set(prev); s.has(u.user_id) ? s.delete(u.user_id) : s.add(u.user_id); return s; });
  };
  const victims = data ? data.users.filter((u) => !keep.has(u.user_id)) : [];
  const victimPhotos = victims.reduce((a, u) => a + (u.photos || 0), 0);

  const execute = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/admin/purge/execute", { keep_user_ids: [...keep], confirm });
      setResult(r);
      toast.success(`${r.deleted_users} compte(s) supprimé(s)`);
      setStep(1); setConfirm("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || e.userMessage || "Échec de la suppression"); } finally { setBusy(false); }
  };

  if (!data) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-[#C68E59] animate-spin" /></div>;

  return (
    <div data-testid="admin-purge-page">
      <div className="mb-6">
        <p className="caption-label text-[#A3483F]">Zone sensible</p>
        <h1 className="font-serif text-3xl sm:text-4xl text-[#1A1A1A] mt-1">Nettoyage de la base</h1>
        <p className="text-sm text-[#8B8680] mt-2 max-w-2xl">Cochez les comptes à <strong>conserver</strong>. Tous les autres seront supprimés avec leurs photos, portfolios, candidatures, collaborations, messages, connexions, participations et notifications. Les événements, concours et contenus éditoriaux sont conservés.</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <Stat label="Comptes" value={data.total} testid="purge-stat-total" />
        <Stat label="Conservés" value={keep.size} tone="green" testid="purge-stat-kept" />
        <Stat label="À supprimer" value={victims.length} tone="red" testid="purge-stat-victims" />
        <Stat label="Photos supprimées" value={victimPhotos} tone="red" testid="purge-stat-photos" />
      </div>

      {result && (
        <div className="mb-6 rounded-2xl bg-[#E7F5EC] border border-[#2E7D32]/30 p-4 text-sm text-[#2E7D32] flex items-start gap-2" data-testid="purge-result">
          <CheckCircle2 className="w-4 h-4 mt-0.5" /> Dernière purge : {result.deleted_users} compte(s) supprimé(s) · {Object.entries(result.details || {}).map(([k, v]) => `${k} ${v}`).join(" · ") || "aucune donnée liée"}
        </div>
      )}

      <DemoPurgeCard onDone={(r) => { setResult(r); load(); }} />

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8B8680]" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un nom ou un e-mail…" className="pl-9 rounded-full" data-testid="purge-search" />
        </div>
        <Button variant="outline" className="rounded-full" onClick={() => setKeep(new Set(data.users.filter((u) => u.locked || u.suggested_keep).map((u) => u.user_id)))} data-testid="purge-reset-suggested">Suggestions</Button>
        <Button variant="outline" className="rounded-full" onClick={() => setKeep(new Set(data.users.map((u) => u.user_id)))} data-testid="purge-keep-all">Tout conserver</Button>
        <Button variant="outline" className="rounded-full" onClick={() => setKeep(new Set(data.users.filter((u) => u.locked).map((u) => u.user_id)))} data-testid="purge-keep-none">Ne garder que les admins</Button>
      </div>

      <div className="bg-white rounded-3xl border border-[#EADDC9] overflow-hidden">
        <ul className="divide-y divide-[#FAF3EA] max-h-[55vh] overflow-y-auto" data-testid="purge-user-list">
          {filtered.map((u) => {
            const kept = keep.has(u.user_id);
            return (
              <li key={u.user_id} className={`flex items-center gap-3 px-4 py-3 ${kept ? "" : "bg-red-50/40"}`} data-testid={`purge-row-${u.user_id}`}>
                <input type="checkbox" checked={kept} disabled={u.locked} onChange={() => toggle(u)} className="w-4 h-4 accent-[#C68E59]" data-testid={`purge-keep-${u.user_id}`} />
                <div className="w-9 h-9 rounded-full bg-[#FAF3EA] overflow-hidden flex items-center justify-center text-sm font-serif text-[#A26F3E] shrink-0">{u.picture ? <img src={u.picture} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} /> : (u.name || "?")[0]}</div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#1A1A1A] truncate">{u.name || "—"} {u.is_demo && <span className="ml-1 text-[10px] uppercase tracking-wider text-[#8B8680]">démo</span>} {u.locked && <Lock className="inline w-3 h-3 text-[#8B8680] ml-1" />}</p>
                  <p className="text-xs text-[#8B8680] truncate">{u.email}</p>
                </div>
                <span className="text-[10px] uppercase tracking-wider text-[#6E4B2A] hidden sm:block">{u.role}</span>
                <span className="text-xs text-[#8B8680] inline-flex items-center gap-1 w-14 justify-end"><Camera className="w-3 h-3" />{u.photos}</span>
                <span className={`text-[10px] font-semibold uppercase tracking-wider w-24 text-right ${kept ? "text-[#2E7D32]" : "text-[#A3483F]"}`}>{kept ? "Conservé" : "Supprimé"}</span>
              </li>
            );
          })}
        </ul>
      </div>

      <div className="mt-8 rounded-3xl border-2 border-[#A3483F]/30 bg-[#FFF5F3] p-6" data-testid="purge-danger-zone">
        <div className="flex items-start gap-3">
          <ShieldAlert className="w-5 h-5 text-[#A3483F] mt-0.5" />
          <div className="flex-1">
            <p className="font-serif text-xl text-[#1A1A1A]">Supprimer {victims.length} compte(s) et {victimPhotos} photo(s)</p>
            <p className="text-sm text-[#6E4B2A] mt-1">Action irréversible. Les comptes conservés : {data.users.filter((u) => keep.has(u.user_id)).map((u) => u.name || u.email).slice(0, 8).join(", ")}{keep.size > 8 ? ` +${keep.size - 8}` : ""}.</p>
            {step === 1 ? (
              <Button className="mt-4 rounded-full bg-[#A3483F] hover:bg-[#8a3a33] text-white" disabled={victims.length === 0} onClick={() => setStep(2)} data-testid="purge-step1-button"><Trash2 className="w-4 h-4 mr-2" /> Lancer la suppression</Button>
            ) : (
              <div className="mt-4 flex flex-col sm:flex-row gap-3 sm:items-center">
                <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Tapez SUPPRIMER pour confirmer" className="max-w-xs rounded-full" data-testid="purge-confirm-input" />
                <Button className="rounded-full bg-[#A3483F] hover:bg-[#8a3a33] text-white" disabled={confirm.trim().toUpperCase() !== "SUPPRIMER" || busy} onClick={execute} data-testid="purge-execute-button">
                  {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />} Confirmer la suppression définitive
                </Button>
                <Button variant="ghost" className="rounded-full" onClick={() => { setStep(1); setConfirm(""); }} data-testid="purge-cancel-button">Annuler</Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function DemoPurgeCard({ onDone }) {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/purge/demos").then(({ data }) => setPreview(data)).catch(() => setPreview({ count: 0, users: [] }));
  useEffect(() => { load(); }, []);

  const run = async () => {
    if (!window.confirm(`Supprimer définitivement ${preview.count} profil(s) démo et leurs photos ?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/purge/demos");
      toast.success(`${data.deleted_users} profil(s) démo supprimé(s)`);
      onDone(data); load();
    } catch (e) { toast.error(e.response?.data?.detail || e.userMessage || "Échec de la suppression"); } finally { setBusy(false); }
  };

  if (!preview) return null;
  return (
    <div className="mb-6 rounded-3xl border border-[#EADDC9] bg-white p-5 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="demo-purge-card">
      <div className="flex-1 min-w-0">
        <p className="caption-label text-[#C68E59]">Profils démo</p>
        <p className="font-serif text-xl text-[#1A1A1A] mt-1">{preview.count === 0 ? "Aucun profil démo restant" : `${preview.count} profil(s) démo détecté(s)`}</p>
        {preview.count > 0 && <p className="text-xs text-[#8B8680] mt-1 truncate">{preview.users.map((u) => u.name || u.email).join(", ")}</p>}
        <p className="text-xs text-[#8B8680] mt-1">Talents fictifs créés pour la démonstration (section « Les visages d'Helencia », Découvrir les talents). Supprimés automatiquement au démarrage du serveur.</p>
      </div>
      <Button className="rounded-full bg-[#1A1A1A] hover:bg-[#333] text-white shrink-0" disabled={busy || preview.count === 0} onClick={run} data-testid="demo-purge-button">
        {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />} Supprimer les profils démo
      </Button>
    </div>
  );
}

function Stat({ label, value, tone, testid }) {
  const color = tone === "red" ? "text-[#A3483F]" : tone === "green" ? "text-[#2E7D32]" : "text-[#1A1A1A]";
  return (
    <div className="bg-white rounded-2xl border border-[#EADDC9] p-4" data-testid={testid}>
      <p className="text-[10px] uppercase tracking-wider text-[#8B8680]">{label}</p>
      <p className={`font-serif text-3xl mt-1 ${color}`}>{value}</p>
    </div>
  );
}
